/**
 * DocumentationPage Component
 * Displays detailed documentation for specific features
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiBook, FiExternalLink, FiStar } from 'react-icons/fi';
import documentationService from '../../services/DocumentationService';
import { categoryOrder } from '../../documentation/constants';
import EditScheduleItemModal from '../EditScheduleItemModal';
import ScheduleEntry from '../../models/ScheduleEntry';

const DocumentationPage = () => {
    const { category, topic } = useParams();
    const navigate = useNavigate();
    const [entry, setEntry] = useState(null);
    const [loading, setLoading] = useState(true);
    const [allEntries, setAllEntries] = useState([]);

    useEffect(() => {
        const loadDocumentation = async () => {
            try {
                if (!documentationService.initialized) {
                    await documentationService.initialize();
                }

                const entries = documentationService.getAllEntries();
                setAllEntries(entries);

                if (category && topic) {
                    // Try to find documentation entry by URL pattern
                    const foundEntry = entries.find(entry =>
                        entry.documentationUrl === `/documentation/${category}/${topic}` ||
                        entry.documentationUrl === `/documentation/${category}`
                    );
                    setEntry(foundEntry);

                    if (foundEntry) {
                        documentationService.trackUsage(foundEntry.id, 'fullDoc');
                    }
                } else {
                    // No specific entry - this is the documentation index
                    setEntry(null);
                }
            } catch (error) {
                console.error('Failed to load documentation:', error);
            } finally {
                setLoading(false);
            }
        };

        loadDocumentation();
    }, [category, topic]);

    // Handle anchor scrolling after content is loaded
    useEffect(() => {
        if (!loading && entry) {
            // Small delay to ensure content is rendered
            const timer = setTimeout(() => {
                const hash = window.location.hash;
                if (hash) {
                    const element = document.getElementById(hash.substring(1));
                    if (element) {
                        element.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start'
                        });
                    }
                }
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [loading, entry]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <span className="text-gray-600">Loading documentation...</span>
                </div>
            </div>
        );
    }

    if (!entry) {
        // If no category/topic specified, show documentation index
        if (!category && !topic) {
            const categorizedEntries = allEntries.reduce((acc, entry) => {
                if (!acc[entry.category]) {
                    acc[entry.category] = [];
                }
                acc[entry.category].push(entry);
                return acc;
            }, {});

            // Order categories according to categoryOrder
            const orderedCategories = categoryOrder.filter(cat => categorizedEntries[cat]);
            // Add any categories not in the order list at the end, but exclude APP_ONLY
            const remainingCategories = Object.keys(categorizedEntries).filter(cat =>
                !categoryOrder.includes(cat) && cat !== 'app-only'
            );
            const finalCategoryOrder = [...orderedCategories, ...remainingCategories];

            return (
                <div className="min-h-screen bg-gray-50">
                    <div className="max-w-6xl mx-auto py-8 px-4">
                        <div className="mb-8">
                            <div className="flex items-center space-x-3 mb-4">
                                <FiBook className="text-blue-600" size={32} />
                                <h1 className="text-4xl font-bold text-gray-900">Documentation</h1>
                            </div>
                            <p className="text-lg text-gray-600">
                                Complete guide to using the schedule management system
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {finalCategoryOrder.map((categoryName) => {
                                const entries = categorizedEntries[categoryName];
                                return (
                                    <div key={categoryName} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                        <h2 className="text-xl font-semibold text-gray-900 mb-4 capitalize">
                                            {categoryName.replace('-', ' ')}
                                        </h2>
                                        <div className="space-y-3">
                                            {entries.map((entry) => (
                                                <Link
                                                    key={entry.id}
                                                    to={entry.documentationUrl}
                                                    className="block p-3 border border-gray-100 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                                                >
                                                    <h3 className="font-medium text-gray-900 mb-1">{entry.title}</h3>
                                                    <p className="text-sm text-gray-600 line-clamp-2">
                                                        {entry.quickHelp ? entry.quickHelp.split('\n')[0] : 'No description available'}
                                                    </p>
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            );
        }

        // Specific documentation not found
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="max-w-md mx-auto text-center">
                    <FiBook className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Documentation Not Found</h2>
                    <p className="text-gray-600 mb-6">
                        The documentation page you're looking for doesn't exist yet.
                    </p>
                    <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                        <FiArrowLeft size={16} />
                        <span>Go Back</span>
                    </button>
                </div>
            </div>
        );
    }

    // Get related documentation entries
    const relatedEntries = allEntries.filter(e =>
        e.category === entry.category && e.id !== entry.id
    ).slice(0, 3);

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-4xl mx-auto py-8 px-4">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700 mb-4 text-sm font-medium"
                    >
                        <FiArrowLeft size={16} />
                        <span>Back</span>
                    </button>

                    <div className="flex items-center space-x-3 mb-4">
                        <FiBook className="text-blue-600" size={24} />
                        <h1 className="text-3xl font-bold text-gray-900">{entry.title}</h1>
                    </div>

                    <div className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                        {entry.category}
                    </div>
                </div>

                {/* Content */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
                    <div className="prose max-w-none">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Overview</h2>
                        <div className="text-gray-700 leading-relaxed mb-6 text-lg">
                            {entry.firstParagraph ? (
                                <div
                                    className="whitespace-pre-line"
                                    dangerouslySetInnerHTML={{
                                        __html: (entry.firstParagraph || '')
                                            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                            .replace(/\*(.*?)\*/g, '<em>$1</em>')
                                    }}
                                />
                            ) : (
                                <p className="whitespace-pre-line">{entry.quickHelp}</p>
                            )}
                        </div>

                        {/* Render detailed content if available */}
                        {entry.content && (
                            <div className="space-y-8">
                                {/* Overview Section (if available) */}
                                {entry.content.overview && (
                                    <div id="overview" className="space-y-4">
                                        <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-2">
                                            Overview
                                        </h2>
                                        <div className="prose max-w-none">
                                            <div
                                                className="text-gray-700 leading-relaxed whitespace-pre-line"
                                                dangerouslySetInnerHTML={{ __html: entry.content.overview.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Setup Steps Navigation (if available) */}
                                {entry.content.setupSteps && (
                                    <div className="border-l-4 border-blue-200 pl-6 py-4 bg-blue-50 rounded-r-lg">
                                        <h3 className="text-lg font-semibold text-blue-900 mb-4">Setup Steps</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            {entry.content.setupSteps.map((step, index) => (
                                                <a
                                                    key={step.link}
                                                    href={step.link}
                                                    className="block p-3 bg-white border border-blue-200 rounded-lg hover:border-blue-400 hover:shadow-sm transition-all"
                                                >
                                                    <div className="flex items-center space-x-2 mb-1">
                                                        <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                                                            {index + 1}
                                                        </span>
                                                        <h4 className="font-medium text-blue-900">{step.title}</h4>
                                                    </div>
                                                    <p className="text-sm text-blue-700 ml-8">{step.description}</p>
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Tips Section */}
                                {entry.content.tips && entry.content.tips.length > 0 && (
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                                        <div className="flex items-center mb-4">
                                            <FiStar className="text-blue-600 mr-3" size={20} />
                                            <h3 className="text-lg font-semibold text-blue-900">Tips & Tricks</h3>
                                        </div>
                                        <ul className="space-y-3 pl-5 list-disc">
                                            {entry.content.tips.map((tip, index) => (
                                                <li key={index} className="text-blue-800">
                                                    {tip.startsWith('Important:') ? (
                                                        <>
                                                            <strong className="font-semibold">Important:</strong>
                                                            {tip.substring('Important:'.length)}
                                                        </>
                                                    ) : (
                                                        tip
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Detailed Sections */}
                                {entry.content.sections && entry.content.sections.map((section) => (
                                    <div key={section.id} id={section.id} className="space-y-4">
                                        <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-2">
                                            {section.title}
                                        </h2>
                                        <div className="prose max-w-none">
                                            <div
                                                className="text-gray-700 leading-relaxed whitespace-pre-line"
                                                dangerouslySetInnerHTML={{ __html: section.description.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}
                                            />
                                        </div>
                                    </div>
                                ))}

                                {/* Quick Start Checklist */}
                                {entry.content.quickStartChecklist && (
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                                        <h3 className="text-lg font-semibold text-green-900 mb-4">Quick Start Checklist</h3>
                                        <ul className="space-y-2">
                                            {entry.content.quickStartChecklist.map((item, index) => (
                                                <li key={index} className="flex items-start space-x-2 text-green-800">
                                                    <span className="text-green-600 mt-0.5">•</span>
                                                    <span>{item}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Troubleshooting */}
                                {entry.content.troubleshooting && (
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                                        <h3 className="text-lg font-semibold text-yellow-900 mb-4">Troubleshooting</h3>
                                        <div className="space-y-4">
                                            {entry.content.troubleshooting.map((item, index) => (
                                                <div key={index} className="border-l-2 border-yellow-400 pl-4">
                                                    <h4 className="font-medium text-yellow-900 mb-1">Issue: {item.issue}</h4>
                                                    <p className="text-yellow-800 text-sm">{item.solution}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Live Demo for Edit Schedule */}
                                {entry.id === 'schedule-edit' && (
                                    <div id="live-demo" className="space-y-4">
                                        <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-2">
                                            Live Demo: EditScheduleItemModal
                                        </h2>
                                        <p className="text-gray-700">
                                            This embedded demo shows the editor with mock data. It’s interactive but does not save.
                                        </p>
                                        <DemoEditScheduleItemModal />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Example content based on the entry type */}
                        {entry.id === 'schedule-unscheduled-container' && (
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-3">How to Use the Available Courses Container</h3>
                                    <ol className="list-decimal list-inside space-y-2 text-gray-700">
                                        <li>Drag courses from the container to calendar time slots to schedule them</li>
                                        <li>Drag scheduled items back to the container to unschedule them</li>
                                        <li>Unscheduled items lose their room and time assignments</li>
                                        <li>Use this to reorganize your schedule flexibly</li>
                                    </ol>
                                </div>

                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Visual Indicators</h3>
                                    <ul className="list-disc list-inside space-y-2 text-gray-700">
                                        <li><span className="text-green-600 font-medium">Green highlight</span> - Valid drop zone when dragging scheduled items</li>
                                        <li><span className="text-blue-600 font-medium">Blue hint</span> - Shows "Drag here to unschedule" message</li>
                                        <li><span className="text-gray-600 font-medium">Course cards</span> - Show subject, teacher, and group information</li>
                                    </ul>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Related Documentation */}
                {relatedEntries.length > 0 && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Related Documentation</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {relatedEntries.map((relatedEntry) => (
                                <Link
                                    key={relatedEntry.id}
                                    to={relatedEntry.documentationUrl}
                                    className="block p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                                >
                                    <h4 className="font-medium text-gray-900 mb-2">{relatedEntry.title}</h4>
                                    <p className="text-sm text-gray-600 line-clamp-2">
                                        {relatedEntry.quickHelp ? relatedEntry.quickHelp.split('\n')[0] : 'No description available'}
                                    </p>
                                    <div className="flex items-center space-x-1 mt-2 text-blue-600 text-sm">
                                        <span>Learn more</span>
                                        <FiExternalLink size={12} />
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DocumentationPage;

// Local demo component for the schedule-edit page
const DemoEditScheduleItemModal = () => {
    const [open, setOpen] = useState(true);

    // Minimal mock catalogs
    const mockTeachers = useMemo(() => ([
        { id: 1, name: 'Alice Johnson' },
        { id: 2, name: 'Bob Smith' },
    ]), []);

    const mockGroups = useMemo(() => ([
        { id: 10, name: 'Group A' },
        { id: 20, name: 'Group B' },
    ]), []);

    const mockSubjects = useMemo(() => ([
        { id: 100, name: 'Mathematics' },
        { id: 200, name: 'Physics' },
    ]), []);

    const mockRooms = useMemo(() => ([
        { id: 1, name: 'Room 101' },
        { id: 2, name: 'Room 202' },
    ]), []);

    // Base schedule items to power conflict detection (keep small)
    const baseItems = useMemo(() => ([
        new ScheduleEntry({
            id: '1001',
            subject: 'Mathematics',
            teacher: 'Alice Johnson',
            groups: [10],
            room: 'Room 101',
            day: 'monday',
            weekSeries: '38,40,42-45',
            year: 2025,
            timeStart: '09:00',
            timeEnd: '10:30',
            type: 'class',
        }),
        new ScheduleEntry({
            id: '1002',
            subject: 'Physics',
            teacher: 'Bob Smith',
            groups: [20],
            room: 'Room 202',
            day: 'monday',
            weekSeries: '38-40',
            year: 2025,
            timeStart: '10:00',
            timeEnd: '11:00',
            type: 'class',
        }),
    ]), []);

    // The selected item being edited
    const selected = useMemo(() => (
        new ScheduleEntry({
            id: '1001',
            subject: 'Mathematics',
            teacher: 'Alice Johnson',
            groups: [10],
            room: 'Room 101',
            day: 'monday',
            weekSeries: '38,40,42-45',
            year: 2025,
            timeStart: '09:00',
            timeEnd: '10:30',
            type: 'class',
            // provide a simple exception to showcase the banner if needed
            exceptions: [
                { week: 40, year: 2025, changes: { timeStart: '11:00', timeEnd: '12:00' } },
            ],
        })
    ), []);

    // No-op save/delete handlers for the demo
    const handleSave = () => {
        alert('Demo: Save invoked (no-op).');
    };
    const handleDelete = () => {
        alert('Demo: Delete invoked (no-op).');
    };

    return (
        <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-gray-600">Embedded demonstration</span>
                <button
                    className="text-blue-600 text-sm underline"
                    onClick={() => setOpen(true)}
                >
                    Open Demo
                </button>
            </div>
            <EditScheduleItemModal
                isOpen={open}
                onClose={() => setOpen(false)}
                selectedScheduleItem={selected}
                onSaveEdit={handleSave}
                onDelete={handleDelete}
                groups={mockGroups}
                subjects={mockSubjects}
                teachers={mockTeachers}
                rooms={mockRooms}
                currentYear={2025}
                currentDate={new Date('2025-09-15')}
                isAdmin={true}
                baseScheduleItems={baseItems}
                renderInline
                modalContainerClassName="mt-2"
            />
        </div>
    );
};